import { useEffect, useState } from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { AlertTriangle, Info, X } from "lucide-react";
import { useApp } from "../../context/AppContext";

/* Brand dialog: gold-bordered glass modal. variant: "default" | "destructive" */
export default function LuxDialog({
  open, onOpenChange, title, description, icon, variant = "default",
  confirmLabel, cancelLabel, onConfirm, busy = false, consequences = [],
  requireText, requireCheck, children, testId = "lux-dialog", confirmDisabled = false,
}) {
  const { t, lang } = useApp();
  const [typed, setTyped] = useState("");
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!open) { setTyped(""); setChecked(false); }
  }, [open]);

  const Icon = icon || (variant === "destructive" ? AlertTriangle : Info);
  const locked =
    confirmDisabled || busy ||
    (requireText && typed.trim() !== requireText) ||
    (requireCheck && !checked);

  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className="lux-overlay" />
        <AlertDialogPrimitive.Content
          className={`lux-modal ${variant}`}
          dir={lang === "ar" ? "rtl" : "ltr"}
          data-testid={testId}
        >
          <button className="lux-modal-x" onClick={() => onOpenChange(false)} aria-label="Close" data-testid={`${testId}-close`}>
            <X size={16} />
          </button>
          <div className={`lux-modal-icon ${variant}`}>
            <Icon size={22} />
          </div>
          <AlertDialogPrimitive.Title className="lux-modal-title" data-testid={`${testId}-title`}>{title}</AlertDialogPrimitive.Title>
          {description && (
            <AlertDialogPrimitive.Description className="lux-modal-desc">{description}</AlertDialogPrimitive.Description>
          )}
          {consequences.length > 0 && (
            <ul className="lux-modal-cons" data-testid={`${testId}-consequences`}>
              {consequences.map((c) => <li key={c}>{c}</li>)}
            </ul>
          )}
          {children}
          {requireText && (
            <label className="lux-modal-field">
              <span>{t("a7.typeToConfirm")} <code>{requireText}</code></span>
              <input
                className="p3-input" dir="ltr" value={typed}
                onChange={(e) => setTyped(e.target.value)}
                data-testid={`${testId}-type-input`}
                autoComplete="off"
              />
            </label>
          )}
          {requireCheck && (
            <label className="lux-modal-check">
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} data-testid={`${testId}-checkbox`} />
              <span>{requireCheck}</span>
            </label>
          )}
          <div className="lux-modal-actions">
            <AlertDialogPrimitive.Cancel asChild>
              <button className="admin-btn" data-testid={`${testId}-cancel`}>{cancelLabel || t("admin.cancel")}</button>
            </AlertDialogPrimitive.Cancel>
            <button
              className={`lux-modal-confirm ${variant}`}
              disabled={locked}
              onClick={onConfirm}
              data-testid={`${testId}-confirm`}
            >
              {busy ? t("admin.saving") : confirmLabel || t("a7.confirm")}
            </button>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
